import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { RateFactor, SaveRateFactorInput } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';

const repo = getTurnoGeoRepository();

type NumKey = 'monthlyHoursDivisor' | 'extraDiurnalFactor' | 'extraNocturnalFactor' | 'extraDiurnalFestiveFactor' | 'extraNocturnalFestiveFactor' | 'nightSurchargeFactor' | 'festiveWithCompFactor' | 'festiveWithoutCompFactor' | 'nightFestiveWithCompFactor' | 'nightFestiveWithoutCompFactor';
const NUM_FIELDS: { key: NumKey; label: string; hint: string }[] = [
  { key: 'monthlyHoursDivisor', label: 'Divisor mensual de horas', hint: 'Horas al mes para el valor de la hora (210 = 42 h/sem, 220 = 44 h, 230 = 46 h).' },
  { key: 'nightSurchargeFactor', label: 'Recargo nocturno', hint: '0,35 = 35 % sobre la hora ordinaria.' },
  { key: 'extraDiurnalFactor', label: 'Extra diurna', hint: '1,25' },
  { key: 'extraNocturnalFactor', label: 'Extra nocturna', hint: '1,75' },
  { key: 'extraDiurnalFestiveFactor', label: 'Extra festiva diurna', hint: 'Ej. 2,15 con dominical al 90 %.' },
  { key: 'extraNocturnalFestiveFactor', label: 'Extra festiva nocturna', hint: 'Ej. 2,65 con dominical al 90 %.' },
  { key: 'festiveWithCompFactor', label: 'Festivo diurno con descanso compensatorio', hint: 'Recargo dominical/festivo: 0,75 / 0,80 / 0,90 según el periodo.' },
  { key: 'festiveWithoutCompFactor', label: 'Festivo diurno sin descanso compensatorio', hint: '1 + el recargo dominical.' },
  { key: 'nightFestiveWithCompFactor', label: 'Festivo nocturno con descanso compensatorio', hint: 'Dominical + recargo nocturno.' },
  { key: 'nightFestiveWithoutCompFactor', label: 'Festivo nocturno sin descanso compensatorio', hint: '1 + dominical + nocturno.' },
];
const EMPTY: Record<string, string> = { effectiveFrom: '', effectiveTo: '', nightStartTime: '19:00', nightEndTime: '06:00', legalReference: '', monthlyHoursDivisor: '210', extraDiurnalFactor: '1.25', extraNocturnalFactor: '1.75', extraDiurnalFestiveFactor: '2.15', extraNocturnalFestiveFactor: '2.65', nightSurchargeFactor: '0.35', festiveWithCompFactor: '0.9', festiveWithoutCompFactor: '1.9', nightFestiveWithCompFactor: '1.25', nightFestiveWithoutCompFactor: '2.25' };
const pct = (n: number) => `${Math.round(n * 100)} %`;

/**
 * Configuración > Factores de recargo (ley). Cada día se liquida con el factor vigente EN SU FECHA:
 * hora de inicio de la jornada nocturna (21:00 → 19:00 desde el 25-dic-2025), recargo nocturno 35 %, extras,
 * dominical/festivo (75 → 80 → 90 %) y el divisor mensual. Cuando cambie la ley, se agrega una fila nueva.
 */
export function RateFactorsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<RateFactor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<RateFactor | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(EMPTY);
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<RateFactor | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listRateFactors().then((r) => { setRows(r); setIsLoading(false); }).catch((e) => { setIsLoading(false); toast.show(e instanceof Error ? e.message : 'No se pudieron cargar los factores.', 'error'); });
  };
  useEffect(load, []);

  const openCreate = () => {
    const last = rows[rows.length - 1];
    setForm(last ? { ...EMPTY, ...toForm(last), effectiveFrom: '', effectiveTo: '', legalReference: '' } : { ...EMPTY });
    setCreating(true);
  };
  const toForm = (r: RateFactor): Record<string, string> => ({
    effectiveFrom: r.effectiveFrom, effectiveTo: r.effectiveTo ?? '', nightStartTime: r.nightStartTime, nightEndTime: r.nightEndTime, legalReference: r.legalReference ?? '',
    monthlyHoursDivisor: String(r.monthlyHoursDivisor), extraDiurnalFactor: String(r.extraDiurnalFactor), extraNocturnalFactor: String(r.extraNocturnalFactor),
    extraDiurnalFestiveFactor: String(r.extraDiurnalFestiveFactor), extraNocturnalFestiveFactor: String(r.extraNocturnalFestiveFactor), nightSurchargeFactor: String(r.nightSurchargeFactor),
    festiveWithCompFactor: String(r.festiveWithCompFactor), festiveWithoutCompFactor: String(r.festiveWithoutCompFactor),
    nightFestiveWithCompFactor: String(r.nightFestiveWithCompFactor), nightFestiveWithoutCompFactor: String(r.nightFestiveWithoutCompFactor),
  });
  const openEdit = (r: RateFactor) => { setForm(toForm(r)); setEditing(r); };
  const close = () => { setCreating(false); setEditing(null); };
  const num = (v: string) => Number(String(v).replace(',', '.'));

  const save = async () => {
    const payload: SaveRateFactorInput = {
      effectiveFrom: form.effectiveFrom, effectiveTo: form.effectiveTo || null, nightStartTime: form.nightStartTime, nightEndTime: form.nightEndTime, legalReference: form.legalReference || null,
    };
    for (const f of NUM_FIELDS) (payload as Record<string, unknown>)[f.key] = num(form[f.key]);
    setIsSaving(true);
    try {
      if (editing) { await repo.updateRateFactor(editing.id, payload); toast.show('Factor actualizado.'); }
      else { await repo.createRateFactor(payload); toast.show('Factor agregado.'); }
      close();
      load();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo guardar.', 'error'); }
    finally { setIsSaving(false); }
  };

  const columns: Column<RateFactor>[] = [
    { key: 'from', filterValue: (r) => r.effectiveFrom, header: 'Vigente desde', render: (r) => <span className="font-medium">{r.effectiveFrom}</span>, sortable: true, sortValue: (r) => r.effectiveFrom },
    { key: 'to', header: 'Hasta', render: (r) => r.effectiveTo ?? <Badge tone="success">Vigente</Badge> },
    { key: 'night', header: 'Jornada nocturna', render: (r) => `${r.nightStartTime} – ${r.nightEndTime}` },
    { key: 'ns', header: 'Recargo nocturno', render: (r) => pct(r.nightSurchargeFactor) },
    { key: 'ex', header: 'Extra diurna / nocturna', render: (r) => `${r.extraDiurnalFactor} / ${r.extraNocturnalFactor}` },
    { key: 'fest', header: 'Dominical / festivo', render: (r) => pct(r.festiveWithCompFactor) },
    { key: 'div', header: 'Divisor', render: (r) => r.monthlyHoursDivisor },
    { key: 'ref', header: 'Norma', render: (r) => r.legalReference ?? '' },
    {
      key: 'actions', header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>Agregar periodo</Button>
      <p className="flex items-start gap-2 text-xs text-ink-500">
        <InfoTip title="Factores por fecha" text="Cada día trabajado se liquida con el factor vigente en esa fecha. Si la ley cambia (por ejemplo el recargo dominical sube al 100 % en julio de 2027), agrega un periodo nuevo con su fecha de inicio: lo anterior no se recalcula distinto." />
        <span>Valores de ley precargados desde julio de 2024 (Ley 2101 de 2021 y Ley 2466 de 2025). Revísalos con tu asesor laboral antes de usar los valores en pesos para pagar.</span>
      </p>
      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="⚖️" title="Sin factores" description="Agrega al menos un periodo para calcular recargos." />
      ) : (
        <DataTable excel fixedKey="from" columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} defaultSortKey="from" defaultSortDir="desc" emptyTitle="Sin factores" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar periodo' : 'Agregar periodo'}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Vigente desde" type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
            <Input label="Hasta (vacío = vigente)" type="date" value={form.effectiveTo} onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })} />
            <Input label="Noche empieza" type="time" value={form.nightStartTime} onChange={(e) => setForm({ ...form, nightStartTime: e.target.value })} />
            <Input label="Noche termina" type="time" value={form.nightEndTime} onChange={(e) => setForm({ ...form, nightEndTime: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {NUM_FIELDS.map((f) => (
              <Input key={f.key} label={f.label} inputMode="decimal" hint={f.hint} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            ))}
          </div>
          <Input label="Norma / referencia" value={form.legalReference} maxLength={250} onChange={(e) => setForm({ ...form, legalReference: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Eliminar periodo"
        description={`¿Eliminar el periodo desde ${deleting?.effectiveFrom}? Las fechas de ese rango quedarán sin factor propio.`}
        tone="danger"
        confirmLabel="Eliminar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) { try { await repo.deleteRateFactor(deleting.id); } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo eliminar.', 'error'); } }
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
